import { State, Selector, Action } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { ProprieteApiService } from './services/propriete-api.service';
import { Router } from '@angular/router';
import { UploadApiService } from '../shared/services/upload-api.service';
import { PhotoUpload } from '../shared/modeles/photo-upload.modele';
import { ProprieteModele } from './modeles/proprieteModel';
import { Injectable } from '@angular/core';

const defaultState = {
  loaded: false,
  pages: ['liste', 'creation', 'detail'],
  currentPage: 'liste',
  proprietes: [],
  selectedProperty: null,
  returnRoute: null,
  lastUploadedImage: null
};

export interface ProprieteStateModel {
  loaded: boolean;
  proprietes: Array<ProprieteModele>;
  pages: Array<string>;
  currentPage: string;
  selectedProperty: ProprieteModele;
  returnRoute: string;
  lastUploadedImage: PhotoUpload;
}

export class ChargerProprietes {
  static type = '[Propriete] ChargerProprietes';
}

export class SelectionnerPropriete {
  static type = '[Propriete] SelectionnerPropriete';
  constructor(public proprieteId: string) {}
}

export class ModifierPropriete {
  static type = '[Propriete] ModifierPropriete';
  constructor(public propriete: ProprieteModele) {}
}

export class EditerFichePropriete {
  static type = '[Propriete] EditerFichePropriete';
  constructor(public proprieteId: string, public returnRoute: string) {}
}

export class SupprimerPropriete {
  static type = '[Propriete] SupprimerPropriete';
  constructor(public proprieteId: string) {}
}

export class CreerPropriete {
  static type = '[Propriete] CreerPropriete';
  constructor(public propriete: ProprieteModele) {}
}

export class SetCurrentPage {
  static type = '[Propriete] SetCurrentPage';
  constructor(public page: string) {}
}

export class ReloadPropriete {
  static type = '[Propriete] ReloadPropriete';
}

export class StoreProrieteDetails {
  static type = '[Propriete] StoreProrieteDetails';
  constructor(public propriete: ProprieteModele) {}
}

export class UploadPhoto {
  static type = '[Propriete] UploadPhoto';
  constructor(public blob: Blob, public originalName: string) {}
}

export class DeletePhoto {
  static type = '[Propriete] DeletePhoto';
  constructor(public propriete: ProprieteModele) {}
}

export class SetProprieteIndicateurRisqueDebugOnly {
  static type = '[Propriete] SetProprieteIndicateurRisqueDebugOnly';
  constructor(public proprieteId: string, public indicateurRisque: any) {}
}

export class CreateProprieteDebugOnly {
  static type = '[Propriete] CreateProprieteDebugOnly';
  constructor(public propriete: ProprieteModele) {}
}

@Injectable()
@State<ProprieteStateModel>({
  name: 'propriete',
  defaults: defaultState
})
export class ProprieteState {
  constructor(
    private readonly _proprieteApiService: ProprieteApiService,
    private readonly _router: Router,
    private readonly _uploadService: UploadApiService
  ) {}

  @Selector()
  static proprietes(state: ProprieteStateModel) {
    return state.proprietes;
  }

  @Selector()
  static isProprietesCreated(state: ProprieteStateModel) {
    return state.proprietes.length > 0;
  }

  @Selector()
  static selectPropertyById(state: ProprieteStateModel) {
    const propertyList: ProprieteModele[] = state.proprietes;
    return (id: string) =>
      !!propertyList && propertyList.length && propertyList.find((property: ProprieteModele) => property.id === id);
  }

  @Selector()
  static selectedProperty(state: ProprieteStateModel) {
    return state.selectedProperty;
  }

  @Action(ChargerProprietes)
  getProprietes({ getState, patchState }) {
    const state = getState();
    if (!state.loaded) {
      return this._proprieteApiService.getProprietes().subscribe((proprietes: ProprieteModele[]) => {
        patchState({
          loaded: true,
          proprietes: proprietes || []
        });
      });
    }
    return null;
  }

  @Action(SelectionnerPropriete)
  selectionnerPropriete({ patchState }, { proprieteId }: SelectionnerPropriete) {
    return this._proprieteApiService.getPropriete(proprieteId).pipe(
      tap(propriete => {
        patchState({
          selectedProperty: propriete
        });
      })
    );
  }

  @Action(ModifierPropriete)
  modifierPropriete({ getState, patchState }, { propriete }: ModifierPropriete) {
    return this._proprieteApiService.updatePropriete(propriete).pipe(
      tap(nouvellePropriete => {
        const state = getState();
        const index = state.proprietes.indexOf(state.proprietes.find(o => o.id === propriete.id));

        const newPropriete = state.proprietes;
        newPropriete[index] = nouvellePropriete;

        patchState({
          proprietes: newPropriete
        });
      })
    );
  }

  @Action(SupprimerPropriete)
  async supprimerPropriete({ getState, patchState }, { proprieteId }: SupprimerPropriete) {
    const state = getState();

    await this._proprieteApiService.deletePropriete(proprieteId).toPromise();

    patchState({
      proprietes: state.proprietes.filter(o => o.id !== proprieteId)
    });
  }

  @Action(CreerPropriete)
  async creerPropriete({ getState, patchState }, { propriete }: CreerPropriete) {
    const state = getState();
    const nouvellePropriete = await this._proprieteApiService.postPropriete(propriete).toPromise();

    if (!nouvellePropriete || !nouvellePropriete.id) {
      throw new Error('Invalid property object');
    }

    patchState({
      proprietes: [nouvellePropriete, ...state.proprietes],
      selectedProperty: null,
      returnRoute: 'propriete/detail/' + nouvellePropriete.id
    });
  }

  @Action(SetCurrentPage)
  setCurrentPage({ getState, patchState }, { page }: SetCurrentPage) {
    const state = getState();
    if (state.pages.includes(page)) {
      patchState({
        currentPage: page
      });
    }
  }

  @Action(EditerFichePropriete)
  editerFichePropriete({ patchState }, { proprieteId, returnRoute }: EditerFichePropriete) {
    patchState({
      returnRoute
    });

    this._router.navigate(['propriete/modifier', proprieteId]);
  }

  @Action(ReloadPropriete)
  reloadPropriete({ dispatch, patchState }) {
    patchState({
      loaded: false
    });

    return dispatch(new ChargerProprietes());
  }

  @Action(UploadPhoto)
  async uploadPhoto({ patchState }, { blob, originalName }: UploadPhoto) {
    const uploadedImage = await this._uploadService.upload(blob, originalName).toPromise();

    patchState({
      lastUploadedImage: uploadedImage
    });
  }

  @Action(DeletePhoto)
  async deletePhoto({ patchState, getState }, { propriete }: DeletePhoto) {
    const state = getState();
    const randomIcon = Math.floor(Math.random() * 8) + 1;

    const requestData = {
      ...propriete,
      photoId: null,
      icone: String(randomIcon)
    };

    const updatedPropriete = await this._proprieteApiService.updatePropriete(requestData).toPromise();

    const index = state.proprietes.indexOf(state.proprietes.find(o => o.id === propriete.id));
    const updatedList = state.proprietes;
    updatedList[index] = updatedPropriete;

    patchState({
      proprietes: updatedList
    });
  }

  @Action(StoreProrieteDetails)
  storeProrieteDetails({ getState, patchState }, { propriete }: StoreProrieteDetails) {
    const state = getState();

    patchState({
      selectedProperty: propriete ? { ...state.selectedProperty, ...propriete } : null
    });
  }

  @Action(SetProprieteIndicateurRisqueDebugOnly)
  setProprieteIndicateurRisqueDebugOnly(
    { getState, patchState },
    { proprieteId, indicateurRisque }: SetProprieteIndicateurRisqueDebugOnly
  ) {
    const state: ProprieteStateModel = getState();

    const proprietes = state.proprietes;
    const foundPropriete = proprietes.find(propriete => propriete.id === proprieteId);
    if (foundPropriete) {
      foundPropriete.analyseEcartPrequalification.indicateurRisque = indicateurRisque;
    }

    patchState({
      proprietes: [...proprietes]
    });
  }

  @Action(CreateProprieteDebugOnly)
  createProprieteDebugOnly({ getState, patchState }, { propriete }: CreateProprieteDebugOnly) {
    const state: ProprieteStateModel = getState();
    const proprietes = state.proprietes;
    proprietes.push(propriete);

    patchState({
      proprietes: [...proprietes]
    });
  }
}
